import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { Progress } from '@/shared/components/ui/progress';
import {
  Printer, Plus, RefreshCw, Search, Filter, Settings, Wifi, WifiOff,
  AlertTriangle, CheckCircle, XCircle, Clock, Trash2, Edit, Play,
  RotateCcw, FileText, Tag, Package, Truck, Box, MapPin, Thermometer,
  Building2, Zap, Bluetooth, Cloud, Usb, Network, Eye, Send, List,
  BarChart3, Activity, History, Layers } from
'lucide-react';

const API_URL = API_BASE || '';

// Status colors
const STATUS_CONFIG = {
  online: { color: 'bg-emerald-500', text: 'text-emerald-500', icon: CheckCircle, label: 'Online' },
  offline: { color: 'bg-slate-400', text: 'text-slate-400', icon: WifiOff, label: 'Offline' },
  busy: { color: 'bg-amber-500', text: 'text-amber-500', icon: Clock, label: 'Busy' },
  error: { color: 'bg-red-500', text: 'text-red-500', icon: AlertTriangle, label: 'Error' },
  paper_out: { color: 'bg-orange-500', text: 'text-orange-500', icon: AlertTriangle, label: 'Paper Out' },
  maintenance: { color: 'bg-violet-500', text: 'text-violet-500', icon: Settings, label: 'Maintenance' }
};

// Job status colors
const JOB_STATUS = {
  queued: { color: 'bg-slate-400', label: 'Queued' },
  processing: { color: 'bg-blue-500', label: 'Processing' },
  printing: { color: 'bg-amber-500', label: 'Printing' },
  completed: { color: 'bg-emerald-500', label: 'Completed' },
  failed: { color: 'bg-red-500', label: 'Failed' },
  cancelled: { color: 'bg-slate-500', label: 'Cancelled' }
};

// Connection type icons
const CONNECTION_ICONS = {
  network: Network,
  usb: Usb,
  bluetooth: Bluetooth,
  cloud: Cloud,
  cups: Network,
  wifi_direct: Wifi
};

// Document type icons
const DOC_TYPE_ICONS = {
  shipping_label: Truck,
  carton_label: Box,
  product_label: Tag,
  bin_label: MapPin,
  compliance_label: Thermometer,
  pallet_label: Layers,
  invoice: FileText,
  packing_slip: FileText,
  pick_list: List
};

// Printer Card Component
const PrinterCard = ({ printer, onEdit, onTest, onDelete }) => {const { t } = useTranslation("wms");
  const status = STATUS_CONFIG[printer.status] || STATUS_CONFIG.offline;
  const StatusIcon = status.icon;
  const ConnIcon = CONNECTION_ICONS[printer.connection_type] || Network;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className={cn('h-1.5', status.color)} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center',
            printer.printer_type === 'label' ? 'bg-blue-500/10' : 'bg-violet-500/10'
            )}>
              <Printer className={cn('h-5 w-5',
              printer.printer_type === 'label' ? 'text-blue-500' : 'text-violet-500'
              )} />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{printer.name}</h3>
              <p className="text-xs text-muted-foreground">{printer.code}</p>
            </div>
          </div>
          <Badge variant="outline" className={cn('text-xs', status.text)}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {status.label}
          </Badge>
        </div>

        <div className="space-y-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" />
            <span>{printer.warehouse_name || 'Unassigned'}</span>
            {printer.zone && <span className="text-muted-foreground/60">• {printer.zone}</span>}
          </div>
          <div className="flex items-center gap-2">
            <ConnIcon className="h-3.5 w-3.5" />
            <span className="capitalize">{printer.connection_type}</span>
            {printer.ip_address && <span className="text-muted-foreground/60">• {printer.ip_address}:{printer.port}</span>}
          </div>
          {printer.manufacturer &&
          <div className="flex items-center gap-2">
              <Settings className="h-3.5 w-3.5" />
              <span>{printer.manufacturer} {printer.model}</span>
            </div>
          }
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">{t("jobs", "Jobs:")}</span>
            <span className="font-medium">{printer.total_jobs || 0}</span>
            <span className="text-emerald-500">({printer.successful_jobs || 0}{t("key_2317", "\u2713)")}</span>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onTest(printer)}>
              <Zap className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(printer)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(printer)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>);

};

// Print Job Row Component
const PrintJobRow = ({ job, onRetry, onCancel }) => {const { t } = useTranslation("wms");
  const status = JOB_STATUS[job.status] || JOB_STATUS.queued;
  const DocIcon = DOC_TYPE_ICONS[job.document_type] || FileText;

  return (
    <div className="flex items-center gap-4 p-3 border-b hover:bg-muted/30 transition-colors">
      <div className={cn('w-2 h-2 rounded-full', status.color)} />
      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
        <DocIcon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">{job.job_id}</p>
          <Badge variant="outline" className="text-[10px]">
            {job.document_type.replace(/_/g, ' ')}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {job.printer_code} • {job.copies} {job.copies > 1 ? 'copies' : 'copy'}
        </p>
      </div>
      <div className="text-right">
        <Badge variant="secondary" className="text-xs">{status.label}</Badge>
        <p className="text-[10px] text-muted-foreground mt-1">
          {job.queued_at ? new Date(job.queued_at).toLocaleTimeString() : ''}
        </p>
      </div>
      <div className="flex gap-1">
        {job.status === 'failed' &&
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onRetry(job)}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        }
        {['queued', 'processing'].includes(job.status) &&
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onCancel(job)}>
            <XCircle className="h-4 w-4" />
          </Button>
        }
      </div>
    </div>);

};

// Create Printer Dialog
const CreatePrinterDialog = ({ isOpen, onClose, onSave }) => {const { t } = useTranslation("wms");
  const [form, setForm] = useState({
    name: '',
    printer_type: 'label',
    connection_type: 'network',
    ip_address: '',
    port: 9100,
    manufacturer: 'Zebra',
    model: '',
    warehouse_id: '',
    warehouse_name: '',
    building: '',
    zone: '',
    dpi: 203
  });

  const handleSave = () => {
    if (!form.name) {
      toast.error('Printer name is required');
      return;
    }
    onSave(form);
    setForm({ name: '', printer_type: 'label', connection_type: 'network', ip_address: '', port: 9100, manufacturer: 'Zebra', model: '', warehouse_id: '', warehouse_name: '', building: '', zone: '', dpi: 203 });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("addNewPrinter", "Add New Printer")}</DialogTitle>
          <DialogDescription>{t("registerANew", "Register a new printer for the print workflow.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>{t("printerName", "Printer Name")}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Shipping Label Printer" />
              
            </div>
            <div className="space-y-2">
              <Label>{t("type", "Type")}</Label>
              <Select value={form.printer_type} onValueChange={(v) => setForm((f) => ({ ...f, printer_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="label">{t("labelPrinter", "Label Printer")}</SelectItem>
                  <SelectItem value="document">{t("documentPrinter", "Document Printer")}</SelectItem>
                  <SelectItem value="receipt">{t("receiptPrinter", "Receipt Printer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("connection", "Connection")}</Label>
              <Select value={form.connection_type} onValueChange={(v) => setForm((f) => ({ ...f, connection_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="network">{t("networkTcpip", "Network (TCP/IP)")}</SelectItem>
                  <SelectItem value="usb">{t("usb", "USB")}</SelectItem>
                  <SelectItem value="bluetooth">{t("bluetooth", "Bluetooth")}</SelectItem>
                  <SelectItem value="cloud">{t("cloudPrint", "Cloud Print")}</SelectItem>
                  <SelectItem value="cups">{t("cupsServer", "CUPS Server")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.connection_type === 'network' &&
          <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>{t("ipAddress", "IP Address")}</Label>
                <Input
                value={form.ip_address}
                onChange={(e) => setForm((f) => ({ ...f, ip_address: e.target.value }))}
                placeholder="192.168.1.100" />
              
              </div>
              <div className="space-y-2">
                <Label>{t("port", "Port")}</Label>
                <Input
                type="number"
                value={form.port}
                onChange={(e) => setForm((f) => ({ ...f, port: parseInt(e.target.value) }))} />
              
              </div>
            </div>
          }

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("manufacturer", "Manufacturer")}</Label>
              <Select value={form.manufacturer} onValueChange={(v) => setForm((f) => ({ ...f, manufacturer: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Zebra">{t("zebra", "Zebra")}</SelectItem>
                  <SelectItem value="Honeywell">{t("honeywell", "Honeywell")}</SelectItem>
                  <SelectItem value="TSC">{t("tsc", "TSC")}</SelectItem>
                  <SelectItem value="Brother">{t("brother", "Brother")}</SelectItem>
                  <SelectItem value="HP">{t("hp", "HP")}</SelectItem>
                  <SelectItem value="Epson">{t("epson", "Epson")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("model", "Model")}</Label>
              <Input
                value={form.model}
                onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
                placeholder="ZT411" />
              
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("dpi", "DPI")}</Label>
            <Select value={String(form.dpi)} onValueChange={(v) => setForm((f) => ({ ...f, dpi: parseInt(v) }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="203">{t("203Dpi", "203 DPI")}</SelectItem>
                <SelectItem value="300">{t("300Dpi", "300 DPI")}</SelectItem>
                <SelectItem value="600">{t("600Dpi", "600 DPI")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="pt-2 border-t">
            <p className="text-sm font-medium mb-3">{t("location", "Location")}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("warehouse", "Warehouse")}</Label>
                <Input
                  value={form.warehouse_name}
                  onChange={(e) => setForm((f) => ({ ...f, warehouse_name: e.target.value }))}
                  placeholder="Main Warehouse" />
                
              </div>
              <div className="space-y-2">
                <Label>{t("building", "Building")}</Label>
                <Input
                  value={form.building}
                  onChange={(e) => setForm((f) => ({ ...f, building: e.target.value }))}
                  placeholder="Building A" />
                
              </div>
              <div className="space-y-2 col-span-2">
                <Label>{t("zone", "Zone")}</Label>
                <Input
                  value={form.zone}
                  onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))}
                  placeholder="Shipping, Receiving, etc." />
                
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
          <Button onClick={handleSave}>{t("addPrinter", "Add Printer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

// Print Dialog Component
const PrintDialog = ({ isOpen, onClose, printers, onPrint }) => {const { t } = useTranslation("wms");
  const [form, setForm] = useState({
    printer_code: '',
    document_type: 'shipping_label',
    copies: 1,
    content_data: {}
  });
  const [previewZpl, setPreviewZpl] = useState('');

  const documentFields = {
    shipping_label: ['company', 'recipient_name', 'address_line1', 'city', 'state', 'zip', 'tracking_number', 'weight', 'carrier', 'order_id'],
    carton_label: ['carton_id', 'order_id', 'shipment_id', 'item_count', 'weight', 'contents', 'destination'],
    product_label: ['product_name', 'sku', 'price', 'uom'],
    bin_label: ['zone', 'aisle', 'rack', 'level', 'bin', 'location_code', 'bin_type', 'capacity']
  };

  const fields = documentFields[form.document_type] || [];

  const handlePrint = () => {
    if (!form.printer_code) {
      toast.error('Please select a printer');
      return;
    }
    onPrint(form);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("printLabel", "Print Label")}</DialogTitle>
          <DialogDescription>{t("configureAndSend", "Configure and send a label to the printer.")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("printer", "Printer")}</Label>
              <Select value={form.printer_code} onValueChange={(v) => setForm((f) => ({ ...f, printer_code: v }))}>
                <SelectTrigger><SelectValue placeholder="Select printer..." /></SelectTrigger>
                <SelectContent>
                  {printers.filter((p) => p.status === 'online').map((p) =>
                  <SelectItem key={p.code} value={p.code}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        {p.name}
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("documentType", "Document Type")}</Label>
              <Select value={form.document_type} onValueChange={(v) => setForm((f) => ({ ...f, document_type: v, content_data: {} }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shipping_label">{t("shippingLabel", "Shipping Label")}</SelectItem>
                  <SelectItem value="carton_label">{t("cartonLabel", "Carton Label")}</SelectItem>
                  <SelectItem value="product_label">{t("productLabel", "Product Label")}</SelectItem>
                  <SelectItem value="bin_label">{t("binlocationLabel", "Bin/Location Label")}</SelectItem>
                  <SelectItem value="compliance_label">{t("complianceLabel", "Compliance Label")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("copies", "Copies")}</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.copies}
                onChange={(e) => setForm((f) => ({ ...f, copies: parseInt(e.target.value) || 1 }))} />
              
            </div>
          </div>

          <div className="space-y-4">
            <Label>{t("labelData", "Label Data")}</Label>
            <ScrollArea className="h-[300px] border rounded-lg p-3">
              <div className="space-y-3">
                {fields.map((field) =>
                <div key={field} className="space-y-1">
                    <Label className="text-xs capitalize">{field.replace(/_/g, ' ')}</Label>
                    <Input
                    value={form.content_data[field] || ''}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      content_data: { ...f.content_data, [field]: e.target.value }
                    }))}
                    placeholder={field}
                    className="h-8 text-sm" />
                  
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel", "Cancel")}</Button>
          <Button onClick={handlePrint}>
            <Send className="h-4 w-4 mr-2" />{t("print", "Print")}

          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

// Main Component
export default function PrintWorkflow() {const { t } = useTranslation("wms");
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');

  // Dialogs
  const [createPrinterOpen, setCreatePrinterOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Get token from localStorage
  const getToken = () => localStorage.getItem('ezerpToken');

  // Fetch data
  const fetchDashboard = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/print/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  }, []);

  const fetchPrinters = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/print/printers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrinters(data.printers);
      }
    } catch (err) {
      console.error('Error fetching printers:', err);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/print/jobs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchPrinters(), fetchJobs()]);
      setLoading(false);
    };
    load();

    // Refresh every 10 seconds
    const interval = setInterval(() => {
      fetchDashboard();
      fetchJobs();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchDashboard, fetchPrinters, fetchJobs]);

  // Handlers
  const handleCreatePrinter = async (data) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/print/printers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        toast.success('Printer added successfully');
        setCreatePrinterOpen(false);
        fetchPrinters();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add printer');
      }
    } catch (err) {
      toast.error('Failed to add printer');
    }
  };

  const handleTestPrinter = async (printer) => {
    const token = getToken();
    toast.loading('Testing printer...');
    try {
      const res = await fetch(`${API_URL}/api/print/printers/${printer.id}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.connectivity.reachable) {
          toast.success(`Printer is online. Test label sent.`);
        } else {
          toast.error(`Printer offline: ${data.connectivity.message}`);
        }
        fetchPrinters();
      }
    } catch (err) {
      toast.error('Failed to test printer');
    }
  };

  const handleDeletePrinter = async (printer) => {
    const token = getToken();
    if (!confirm(`Delete printer "${printer.name}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/print/printers/${printer.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Printer deleted');
        fetchPrinters();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Failed to delete printer');
    }
  };

  const handlePrint = async (data) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/print/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        const job = await res.json();
        toast.success(`Print job ${job.job_id} submitted`);
        setPrintDialogOpen(false);
        fetchJobs();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit print job');
      }
    } catch (err) {
      toast.error('Failed to submit print job');
    }
  };

  const handleRetryJob = async (job) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/print/jobs/${job.job_id}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Job requeued');
        fetchJobs();
      }
    } catch (err) {
      toast.error('Failed to retry job');
    }
  };

  const handleCancelJob = async (job) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/print/jobs/${job.job_id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Job cancelled');
        fetchJobs();
      }
    } catch (err) {
      toast.error('Failed to cancel job');
    }
  };

  // Filter printers
  const filteredPrinters = printers.filter((p) => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) && !p.code.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (warehouseFilter !== 'all' && p.warehouse_id !== warehouseFilter) return false;
    return true;
  });

  // Get unique warehouses
  const warehouses = [...new Set(printers.map((p) => p.warehouse_name).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="print-workflow-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("printWorkflow", "Print Workflow")}</h1>
          <p className="text-muted-foreground">{t("managePrintersPrint", "Manage printers, print jobs, and templates")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => {fetchPrinters();fetchJobs();fetchDashboard();}}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" onClick={() => setPrintDialogOpen(true)}>
            <Send className="h-4 w-4 mr-2" />{t("printLabel", "Print Label")}

          </Button>
          <Button onClick={() => setCreatePrinterOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />{t("addPrinter", "Add Printer")}

          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboard &&
      <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Printer className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.printers.total}</p>
                  <p className="text-xs text-muted-foreground">{t("totalPrinters", "Total Printers")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.printers.online}</p>
                  <p className="text-xs text-muted-foreground">{t("online", "Online")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-violet-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.jobs_24h.total}</p>
                  <p className="text-xs text-muted-foreground">{t("jobs24h", "Jobs (24h)")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.jobs_24h.pending}</p>
                  <p className="text-xs text-muted-foreground">{t("pending", "Pending")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.jobs_24h.success_rate}%</p>
                  <p className="text-xs text-muted-foreground">{t("successRate", "Success Rate")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* Main Content */}
      <Tabs defaultValue="printers" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="printers" data-testid="printers-tab">
              <Printer className="h-4 w-4 mr-2" />{t("printers", "Printers (")}
              {printers.length})
            </TabsTrigger>
            <TabsTrigger value="queue" data-testid="queue-tab">
              <List className="h-4 w-4 mr-2" />{t("printQueue", "Print Queue (")}
              {jobs.length})
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <FileText className="h-4 w-4 mr-2" />{t("templates", "Templates")}

            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-[200px]" />
              
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="online">{t("online", "Online")}</SelectItem>
                <SelectItem value="offline">{t("offline", "Offline")}</SelectItem>
                <SelectItem value="error">{t("error", "Error")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Printers Tab */}
        <TabsContent value="printers" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPrinters.map((printer) =>
            <PrinterCard
              key={printer.id}
              printer={printer}
              onEdit={() => {}}
              onTest={handleTestPrinter}
              onDelete={handleDeletePrinter} />

            )}
          </div>
          {filteredPrinters.length === 0 &&
          <div className="text-center py-12 text-muted-foreground">
              <Printer className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>{t("noPrintersFound", "No printers found")}</p>
            </div>
          }
        </TabsContent>

        {/* Queue Tab */}
        <TabsContent value="queue" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{t("printQueue", "Print Queue")}</CardTitle>
              <CardDescription>{t("recentPrintJobs", "Recent print jobs and their status")}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                {jobs.length === 0 ?
                <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>{t("noPrintJobs", "No print jobs")}</p>
                  </div> :

                jobs.map((job) =>
                <PrintJobRow
                  key={job.id}
                  job={job}
                  onRetry={handleRetryJob}
                  onCancel={handleCancelJob} />

                )
                }
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
            { code: 'shipping_label', name: 'Shipping Label', desc: '4x6 shipping label with tracking', icon: Truck },
            { code: 'carton_label', name: 'Carton Label', desc: '4x4 carton content label', icon: Box },
            { code: 'product_label', name: 'Product Label', desc: '2x1 product barcode label', icon: Tag },
            { code: 'bin_label', name: 'Bin Label', desc: '3x2 bin/location label', icon: MapPin },
            { code: 'compliance_label', name: 'Compliance Label', desc: '4x3 cold chain/hazmat label', icon: Thermometer },
            { code: 'pallet_label', name: 'Pallet Label', desc: '4x6 pallet identification', icon: Layers }].
            map((template) => {
              const Icon = template.icon;
              return (
                <Card key={template.code} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <Icon className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{template.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{template.desc}</p>
                        <Badge variant="outline" className="mt-2 text-[10px]">{t("zpl", "ZPL")}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>);

            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreatePrinterDialog
        isOpen={createPrinterOpen}
        onClose={() => setCreatePrinterOpen(false)}
        onSave={handleCreatePrinter} />
      

      <PrintDialog
        isOpen={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        printers={printers}
        onPrint={handlePrint} />
      
    </div>);

}
